import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  AppointmentDayAvailability,
  AppointmentPreselection,
  AppointmentSlot,
  BookingConfirmation,
  ConsentState,
  FlowState,
  PatientDetails
} from '../../page/appointments/model/appointments-models';
import {FlowStep} from '../model/flow-step';

const STORAGE_KEY = 'polya-appointments-flow';

@Injectable({
  providedIn: 'root'
})
export class AppointmentsFlowStateService {
  private readonly stateSubject = new BehaviorSubject<FlowState>(this.loadState());
  readonly state$ = this.stateSubject.asObservable();

  get snapshot(): FlowState {
    return this.stateSubject.value;
  }

  updatePreselection(preselection: AppointmentPreselection) {
    this.patchState({
      preselection,
      selectedSlot: null,
      selectedDay: null,
      patientDetails: null,
      consents: null,
      verificationCompleted: false,
      bookingConfirmation: null,
      smsBlockedUntil: null,
      smsResendCount: 0,
      smsLastSentAt: null
    });
  }

  updateSelectedSlot(day: AppointmentDayAvailability, slot: AppointmentSlot) {
    this.patchState({
      selectedDay: day,
      selectedSlot: slot,
      patientDetails: null,
      consents: null,
      verificationCompleted: false,
      bookingConfirmation: null,
      smsBlockedUntil: null,
      smsResendCount: 0,
      smsLastSentAt: null
    });
  }

  updateSelectedDay(day: AppointmentDayAvailability) {
    this.patchState({
      selectedDay: day,
      selectedSlot: null,
      patientDetails: null,
      consents: null,
      verificationCompleted: false,
      bookingConfirmation: null,
      smsBlockedUntil: null,
      smsResendCount: 0,
      smsLastSentAt: null
    });
  }

  updatePatientDetails(details: PatientDetails, consents: ConsentState) {
    this.patchState({
      patientDetails: details,
      consents,
      verificationCompleted: false,
      bookingConfirmation: null
    });
  }

  markVerificationCompleted() {
    this.patchState({verificationCompleted: true});
  }

  setBookingConfirmation(confirmation: BookingConfirmation) {
    this.patchState({bookingConfirmation: confirmation});
  }

  setSmsRateLimit(blockedUntil: string | null, resendCount: number, lastSentAt: string | null) {
    this.patchState({
      smsBlockedUntil: blockedUntil,
      smsResendCount: resendCount,
      smsLastSentAt: lastSentAt
    });
  }

  clearAll() {
    this.stateSubject.next(this.createInitialState());
    this.persistState();
  }

  hasReached(step: FlowStep): boolean {
    switch (step) {
      case FlowStep.Preselection:
        return this.isPreselectionComplete();
      case FlowStep.Calendar:
        return this.isPreselectionComplete() && this.isSlotComplete();
      case FlowStep.PatientData:
        return this.isPreselectionComplete() && this.isSlotComplete() && this.isPatientDataComplete();
      case FlowStep.Verification:
        return this.isPreselectionComplete()
          && this.isSlotComplete()
          && this.isPatientDataComplete()
          && this.snapshot.verificationCompleted;
      case FlowStep.Success:
        return !!this.snapshot.bookingConfirmation;
      default:
        return false;
    }
  }

  getRedirectRoute(requiredStep: FlowStep): string {
    if (!this.isPreselectionComplete()) {
      return '/vorauswahl';
    }
    if (requiredStep === FlowStep.Calendar) {
      return '/kalender';
    }
    if (!this.isSlotComplete()) {
      return '/kalender';
    }
    if (requiredStep === FlowStep.PatientData) {
      return '/patient';
    }
    if (!this.isPatientDataComplete()) {
      return '/patient';
    }
    if (requiredStep === FlowStep.Verification) {
      return '/verifizierung';
    }
    if (!this.snapshot.verificationCompleted) {
      return '/verifizierung';
    }
    return '/erfolg';
  }

  private isPreselectionComplete(): boolean {
    const {preselection} = this.snapshot;
    return !!preselection.insurance && !!preselection.appointmentType && !!preselection.patientStatus;
  }

  private isSlotComplete(): boolean {
    return !!this.snapshot.selectedSlot && !!this.snapshot.selectedDay;
  }

  private isPatientDataComplete(): boolean {
    if (!this.snapshot.patientDetails || !this.snapshot.consents?.privacyAccepted) {
      return false;
    }
    if (this.snapshot.patientDetails.forOtherPerson) {
      return !!this.snapshot.consents?.proxyAccepted;
    }
    return true;
  }

  private patchState(partial: Partial<FlowState>) {
    const next = {...this.snapshot, ...partial};
    this.stateSubject.next(next);
    this.persistState();
  }

  private persistState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.snapshot));
    } catch {
      // ignore persistence errors
    }
  }

  private loadState(): FlowState {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as FlowState;
        if (parsed && parsed.preselection) {
          return {...this.createInitialState(), ...parsed};
        }
      }
    } catch {
      // ignore parse errors
    }
    return this.createInitialState();
  }

  private createInitialState(): FlowState {
    return {
      preselection: {
        insurance: null,
        appointmentType: null,
        patientStatus: null
      },
      selectedSlot: null,
      selectedDay: null,
      patientDetails: null,
      consents: null,
      verificationCompleted: false,
      bookingConfirmation: null,
      smsBlockedUntil: null,
      smsResendCount: 0,
      smsLastSentAt: null
    };
  }
}
